import React from 'react';

// ---------------------------------------------------------------- #
// ConnectionAvatar
// ---------------------------------------------------------------- #
//
// The coloured circle with two initials shown at the left of each Xero
// organisation, MYOB company file and Facebook user on the wizards' cards, so
// they are easy to tell apart at a glance. With no name, it shows a person
// icon. The colour comes from the name, so a name always gets the same colour.
//
// Props:
//   name   The organisation, company file or Facebook user name, for example "Edwards Hotels Pty Ltd"

// ---------------------------------------------------------------- #
// Constants
// ---------------------------------------------------------------- #

/** Background and text colour pairs; each pair passes 4.5:1 contrast. */
const AVATAR_COLOURS = [
  { background: '#e3f2fd', text: '#0b4f86' },
  { background: '#e6f4ea', text: '#1b5e20' },
  { background: '#fff3e0', text: '#8a4b00' },
  { background: '#f3e5f5', text: '#6a1b9a' },
  { background: '#e0f7fa', text: '#00606b' },
  { background: '#fce4ec', text: '#8e1846' },
  { background: '#ede7f6', text: '#4527a0' },
  { background: '#eceff1', text: '#37474f' }
];

/** Words skipped when picking initials, so "The Ian Edwards Family Trust" reads "IE", not "TI". */
const SKIPPED_WORDS = ['the', 'a', 'an', 'and', '&'];

// ---------------------------------------------------------------- #
// Helpers
// ---------------------------------------------------------------- #

/**
 * The first letters of the first two meaningful words, for example "Edwards Hotels Pty Ltd" gives "EH".
 */
const pickInitials = (name) => {
  // Split the name into words, dropping empty pieces
  const allWords = String(name || '').trim().split(/\s+/);
  const words = [];
  for (const word of allWords) {
    const isSkipped = SKIPPED_WORDS.includes(word.toLowerCase());
    if (word === '' || isSkipped) {
      continue;
    }
    words.push(word);
  }

  // No usable words: a question mark
  if (words.length === 0) {
    return '?';
  }

  // One word: its first two letters
  if (words.length === 1) {
    return words[0].substring(0, 2).toUpperCase();
  }

  // Two or more words: the first letter of each of the first two
  const firstLetter = words[0].charAt(0);
  const secondLetter = words[1].charAt(0);
  return `${firstLetter}${secondLetter}`.toUpperCase();
};

/**
 * Pick a colour pair from the name, so a name always gets the same colour.
 */
const pickColours = (name) => {
  // Add up the character codes of the name
  let total = 0;
  const text = String(name || '');
  for (let index = 0; index < text.length; index++) {
    total += text.charCodeAt(index);
  }

  // Use the total to choose one of the pairs
  const colourIndex = total % AVATAR_COLOURS.length;
  return AVATAR_COLOURS[colourIndex];
};

// ---------------------------------------------------------------- #
// The avatar
// ---------------------------------------------------------------- #

/**
 * A coloured circle holding a connection's two initials. It is decoration: the name is shown beside it.
 */
const ConnectionAvatar = ({ name }) => {
  const initials = pickInitials(name);
  const colours = pickColours(name);
  const circleStyle = { backgroundColor: colours.background, color: colours.text };

  // No name to take initials from: a person icon instead of a question mark
  const hasNoName = initials === '?';
  if (hasNoName) {
    return (
      <span className="integrations-connection-avatar" style={circleStyle} aria-hidden="true">
        <i className="icon-base ti tabler-user"></i>
      </span>
    );
  }

  return (
    <span className="integrations-connection-avatar" style={circleStyle} aria-hidden="true">
      {initials}
    </span>
  );
};

export default ConnectionAvatar;
